import cx from 'classnames';
import { formatDistanceStrict } from "date-fns";

import { Badge } from "components/Badge";
import { TextEllipsis } from "components/TextEllipsis";

import { strToDateTime } from "utils/Datetime";

import { ContactName, Container, Info, MessagesCount, NameRow, Nps, ProfilePicture, SearchedMessage } from "./styles";

import { ContactProps } from "./types";

import avatarImg from '../../assets/avatar.jpg';

import { ArchiveContactModal } from "components/ArchiveContactModal";
import { EditContactModal } from "components/EditContactModal";
import { ImageModal } from "components/ImageModal";
import { pt } from 'date-fns/locale';
import { useContacts } from "hooks/useContacts";
import { useArchiveGuard } from "hooks/useArchiveGuard";
import { MouseEvent, useState } from "react";
import { Archive, Shield } from "react-feather";
import { useTheme } from 'styled-components';
import { Button } from "styles/Button";
import { Count } from "styles/Count";
import { Muted } from "styles/Muted";
import { Contact as ContactType } from "types/Contact";
import { useAuth } from 'hooks/useAuth';
import { TagView } from 'components/TagView';

export function Contact({
  contact,
  startedOption,
}: ContactProps) {
  const { name, nps, id, archived_at, image_url, last_seen, new_messages_count, user, category, message, tag, stage_cs, last_received_at, talk_started_at, user_company_id, real_estate_id, is_admin } = contact;

  const [isArchiveContactModalOpen, setIsArchiveContactModalOpen] = useState(false);
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] = useState(false);

  const { selectedContact, selectContact } = useContacts();

  const { user: loggedUser } = useAuth();

  const resolveArchiveAction = useArchiveGuard();

  const theme = useTheme();

  function handleCloseProfilePictureModal() {
    setIsProfilePictureModalOpen(false);
  }

  function handleOpenProfilePictureModal() {
    setIsProfilePictureModalOpen(true);
  }

  function handleContactProfilePictureClick(e: MouseEvent<HTMLImageElement>) {
    e.stopPropagation();
    handleOpenProfilePictureModal();
  }

  async function handleContactSelection(contact: ContactType) {
    await selectContact(contact);
  }

  async function handleOpenArchiveModal() {
    const choice = await resolveArchiveAction({
      userCompanyId: user_company_id,
      realEstateId: real_estate_id,
    });

    if (choice === 'archive') setIsArchiveContactModalOpen(true);
    else if (choice === 'associate') setIsEditContactModalOpen(true);
  }

  function handleCloseArchiveModal() {
    setIsArchiveContactModalOpen(false);
  }

  const interactionStatus = () => {
    if (!last_received_at) return "Sem interação recente";

    const lastReceivedDate = new Date(last_received_at);
    const now = new Date();
    const hoursDifference = Math.abs(now.getTime() - lastReceivedDate.getTime()) / 36e5;

    return hoursDifference > 24 ? "Sem interação recente" : '';
  };

  const started_at_date = () => {
    if (!user && !archived_at && talk_started_at) {
      return talk_started_at;
    }
    return last_seen ?? '';
  };

  return (
    <>
      <Container
        className={cx({
          selected: id === selectedContact.id,
          started: !(contact.archived_at || contact.user) && 1 === startedOption,
          minutes10: !(contact.archived_at || contact.user) && 2 === startedOption,
          minutes15: !(contact.archived_at || contact.user) && 3 === startedOption,
        })}
        key={id}
        onClick={() => handleContactSelection(contact)}
      >
        <td>
          <ProfilePicture>
            <img
              src={image_url ?? avatarImg}
              alt={name} loading="lazy"
              onClick={handleContactProfilePictureClick}
              onError={(e) => e.currentTarget.src = avatarImg}
            />
            {new_messages_count > 0 && (
              <MessagesCount>
                <Count>{new_messages_count}</Count>
              </MessagesCount>
            )}
          </ProfilePicture>
          <Info>
            <div>
              <NameRow>
                <ContactName width={150}>
                  {name}
                </ContactName>
                {user_company_id && is_admin && (
                  <span title="Administrador">
                    <Shield color={theme.colors.primary} size="12" />
                  </span>
                )}
              </NameRow>
              <Nps width={150}>

                {nps != null ? 'NPS ' + JSON.parse(nps).nps : ''}
              </Nps>
              <TextEllipsis width={200}>
                {interactionStatus()}
              </TextEllipsis>
              {!!message?.body && (
                <SearchedMessage>
                  <TextEllipsis width={200}>
                    Msg: {message.body}
                  </TextEllipsis>
                </SearchedMessage>
              )}
            </div>
          </Info>
        </td>
        <td title={started_at_date() ? strToDateTime(started_at_date()!) : 'última interação não registrada'}>
          <TextEllipsis
            width={110}
            tooltip={started_at_date() ? strToDateTime(started_at_date()!) : 'última interação não registrada'}
          >
            {started_at_date()
              ? formatDistanceStrict(
                new Date(started_at_date()!),
                new Date(),
                { addSuffix: true, locale: pt }
              )
              : <Muted>N/A</Muted>
            }
            {stage_cs && (
              <>
                <br />
                Estágio: <TagView backgroundColor="#029306" color="#FFFFFF">{stage_cs}</TagView>
              </>
            )}
          </TextEllipsis>
        </td>
        <td>
          <TextEllipsis width={100}>
            {user?.name ?? <Muted>N/A</Muted>}
          </TextEllipsis>
          <br />
          {tag?.name != null ? <TagView>{tag.name}</TagView> : ''}
        </td>
        <td>
          {category?.name ? <Badge>{category?.name}</Badge> : <Muted>N/A</Muted>}
        </td>
        <td>
          {(user?.id === loggedUser?.id || !user?.id) && (
            <Button disabled={!!archived_at} onClick={handleOpenArchiveModal} small accent>
              <Archive color={theme.colors.buttonText} size="12" />
            </Button>
          )}
        </td>
      </Container >

      {isProfilePictureModalOpen && <ImageModal
        isOpen={isProfilePictureModalOpen}
        onRequestClose={handleCloseProfilePictureModal}
        imageUrl={image_url ?? avatarImg}
      />}

      <ArchiveContactModal
        contact={{ id, name, categoryId: category?.id, responsibleId: user?.id, userCompanyId: user_company_id, realEstateId: real_estate_id }}
        isOpen={isArchiveContactModalOpen}
        onRequestClose={handleCloseArchiveModal}
      />

      <EditContactModal
        isOpen={isEditContactModalOpen}
        onRequestClose={() => setIsEditContactModalOpen(false)}
        contactToEdit={contact}
      />
    </>
  );
}
